/**
 * Tenants Tab - Superadmin tenant management
 *
 * Lists all tenants with expandable cards showing app module toggles.
 * Allows superadmins to enable/disable modules per tenant.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listTenants,
  getTenantFeatures,
  setTenantFeature,
  type Tenant,
} from '@/services/adminApi';
import apiClient from '@/services/api';

const APP_MODULE_KEYS = ['calendar', 'email', 'knowledge_base', 'cogito', 'tasks', 'disk', 'people', 'chat', 'contractors', 'eink_mode'] as const;

interface TenantFeaturesState {
  features: Record<string, boolean>;
  loading: boolean;
}

interface CreateTenantForm {
  id: string;
  name: string;
  domain: string;
  tenant_type: 'standard' | 'institutional';
}

export default function TenantsTab() {
  const { t } = useTranslation('admin');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);
  const [tenantFeatures, setTenantFeatures] = useState<Record<string, TenantFeaturesState>>({});
  const [togglingKeys, setTogglingKeys] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTenantForm>({
    id: '', name: '', domain: '', tenant_type: 'standard',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listTenants();
      setTenants(data.tenants);
    } catch {
      setError(t('tenants.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = async (tenantId: string) => {
    if (expandedTenantId === tenantId) {
      setExpandedTenantId(null);
      return;
    }

    setExpandedTenantId(tenantId);

    // Load features if not already loaded
    if (!tenantFeatures[tenantId]) {
      setTenantFeatures(prev => ({
        ...prev,
        [tenantId]: { features: {}, loading: true }
      }));

      try {
        const data = await getTenantFeatures(tenantId);
        setTenantFeatures(prev => ({
          ...prev,
          [tenantId]: { features: data.features, loading: false }
        }));
      } catch {
        setTenantFeatures(prev => ({
          ...prev,
          [tenantId]: { features: {}, loading: false }
        }));
      }
    }
  };

  const handleToggleFeature = async (tenantId: string, featureKey: string, currentEnabled: boolean) => {
    const toggleKey = `${tenantId}:${featureKey}`;
    if (togglingKeys.has(toggleKey)) return;

    setTogglingKeys(prev => new Set(prev).add(toggleKey));

    // Optimistic update
    const newEnabled = !currentEnabled;
    setTenantFeatures(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        features: { ...prev[tenantId].features, [featureKey]: newEnabled }
      }
    }));

    try {
      await setTenantFeature(tenantId, featureKey, newEnabled);
    } catch {
      // Revert on failure
      setTenantFeatures(prev => ({
        ...prev,
        [tenantId]: {
          ...prev[tenantId],
          features: { ...prev[tenantId].features, [featureKey]: currentEnabled }
        }
      }));
    } finally {
      setTogglingKeys(prev => {
        const next = new Set(prev);
        next.delete(toggleKey);
        return next;
      });
    }
  };

  const getFeatureEnabled = (tenantId: string, key: string): boolean => {
    const state = tenantFeatures[tenantId];
    if (!state) return true;
    // Not in features dict = enabled by default (opt-out model)
    return state.features[key] !== false;
  };

  const getEnabledCount = (tenantId: string): number => {
    return APP_MODULE_KEYS.filter(key => getFeatureEnabled(tenantId, key)).length;
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setMessage(null);
    try {
      await apiClient.post('/admin/tenants', createForm);
      setMessage({ type: 'success', text: t('tenants.createSuccess') });
      setShowCreateForm(false);
      setCreateForm({ id: '', name: '', domain: '', tenant_type: 'standard' });
      await loadTenants();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage({ type: 'error', text: detail || t('tenants.createFailed') });
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTenantTypeBadge = (tenantType?: string) => {
    if (tenantType === 'institutional') {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">{t('tenants.institutional')}</span>;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadTenants}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          {t('agents.retry')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('tenants.title')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('tenants.description')}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
        >
          {showCreateForm ? t('common.cancel') : t('tenants.createTenant')}
        </button>
      </div>

      {/* Create Tenant Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateTenant} className="bg-white rounded-lg border border-gray-200 p-5 mb-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants.tenantId')}</label>
              <input
                type="text"
                value={createForm.id}
                onChange={e => setCreateForm({ ...createForm, id: e.target.value })}
                placeholder="acme-corp"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants.tenantName')}</label>
              <input
                type="text"
                value={createForm.name}
                onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Acme Corp"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants.tenantDomain')}</label>
              <input
                type="text"
                value={createForm.domain}
                onChange={e => setCreateForm({ ...createForm, domain: e.target.value })}
                placeholder="acme.platform.maiabrain.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tenants.tenantType')}</label>
              <select
                value={createForm.tenant_type}
                onChange={e => setCreateForm({ ...createForm, tenant_type: e.target.value as 'standard' | 'institutional' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white"
              >
                <option value="standard">{t('tenants.typeStandard')}</option>
                <option value="institutional">{t('tenants.typeInstitutional')}</option>
              </select>
              {createForm.tenant_type === 'institutional' && (
                <p className="text-xs text-teal-600 mt-1">{t('tenants.institutionalHint')}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
            >
              {isCreating ? t('common.loading') : t('tenants.createTenant')}
            </button>
          </div>
        </form>
      )}

      {tenants.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('tenants.noTenants')}
        </div>
      ) : (
        <div className="space-y-3">
          {tenants.map(tenant => {
            const isExpanded = expandedTenantId === tenant.id;
            const featuresState = tenantFeatures[tenant.id];
            const enabledCount = featuresState ? getEnabledCount(tenant.id) : APP_MODULE_KEYS.length;
            const allEnabled = enabledCount === APP_MODULE_KEYS.length;

            return (
              <div
                key={tenant.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                {/* Tenant Header - Clickable */}
                <button
                  onClick={() => handleExpand(tenant.id)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Expand chevron */}
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>

                    {/* Tenant info */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{tenant.name}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(tenant.status)}`}>
                          {tenant.status}
                        </span>
                        {getTenantTypeBadge(tenant.tenant_type)}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{tenant.domain}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    {/* Module summary */}
                    <span className={`text-xs px-2 py-1 rounded ${allEnabled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      {allEnabled
                        ? t('tenants.allModulesEnabled')
                        : t('tenants.modulesRestricted', { enabled: enabledCount, total: APP_MODULE_KEYS.length })
                      }
                    </span>

                    {/* User count */}
                    <span className="text-sm text-gray-500">
                      {t('tenants.userCount', { count: tenant.user_count })}
                    </span>
                  </div>
                </button>

                {/* Expanded: Feature Toggles */}
                {isExpanded && (
                  <div className="border-t border-gray-200 px-5 py-4 bg-gray-50">
                    {featuresState?.loading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                      </div>
                    ) : (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('tenants.appModules')}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {APP_MODULE_KEYS.map(key => {
                            const enabled = getFeatureEnabled(tenant.id, key);
                            const toggleKey = `${tenant.id}:${key}`;
                            const isToggling = togglingKeys.has(toggleKey);

                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-gray-200"
                              >
                                <span className="text-sm text-gray-700">{t(`tenants.${key}`)}</span>
                                <button
                                  onClick={() => handleToggleFeature(tenant.id, key, enabled)}
                                  disabled={isToggling}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                    enabled ? 'bg-indigo-600' : 'bg-gray-300'
                                  } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      enabled ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
